import { useCallback, useMemo } from 'react';
import { useGetEditorThread } from './useGetEditorThread';
import { useGetUser } from '../userHooks';
import { useUpdateEditorThreadMutation } from 'src/store/services';
import { ArtifactType, ProgrammingLanguageOptions } from 'src/types';
import { useEditorState } from './useEditorState';
import { useEditorAutoSavingStatus } from './useEditorAutoSavingStatus';

export const useCodeAutoSave = () => {
  const { editorThread } = useGetEditorThread();
  const { user_id } = useGetUser();
  const { codeEditorData } = useEditorState();
  const { onChangeAutoSavingStatus } = useEditorAutoSavingStatus();

  const [updateEditorThread] = useUpdateEditorThreadMutation();

  const onCodeAutoSave = useCallback(
    async (codeContent: string) => {
      onChangeAutoSavingStatus('loading');

      const contents =
        !!editorThread?.artifact?.contents &&
        editorThread.artifact.contents.length > 0
          ? editorThread.artifact.contents.map((item) => {
              if (item.index === editorThread?.artifact?.currentIndex) {
                return { ...item, code: codeContent };
              } else {
                return item;
              }
            })
          : [
              {
                index: 1,
                type: ArtifactType.CODE,
                code: codeContent,
                language:
                  (codeEditorData?.language as ProgrammingLanguageOptions) ||
                  ProgrammingLanguageOptions.OTHER,
              },
            ];

      try {
        const result = await updateEditorThread({
          user_id,
          thread_id: editorThread?.thread_id || '',
          thread: {
            ...editorThread,
            artifact: {
              ...editorThread?.artifact,
              contents,
            },
          },
        }).unwrap();

        if (result) {
          onChangeAutoSavingStatus('success');
        }
      } catch (error) {
        onChangeAutoSavingStatus('error');
      } finally {
        onChangeAutoSavingStatus(null);
      }
    },
    [
      onChangeAutoSavingStatus,
      editorThread,
      codeEditorData?.language,
      updateEditorThread,
      user_id,
    ],
  );

  return useMemo(() => ({ onCodeAutoSave }), [onCodeAutoSave]);
};
